import { MEDIA_BATCH_SIZE, type PresentationMediaResponse } from '../../reports/api/presentation-media';
import { isTikTokUrl, parseTikTokVideoUrl } from '../../../shared/api/tiktok.api';
import type { MediaStats, MediaStatsError } from '../../statistics/types/statistics';
import { extractShortcode } from '../../statistics/lib/parse-instagram-url';
import { cleanPermalink } from '../../statistics/lib/top-videos';
import {
  groupSeeding,
  makeSeedingGroup,
  pickTopSeeding,
  type SeedingGroup,
  type SeedingItem,
} from './group-seeding';
import type { SeedingInput } from './parse-seeding-input';

/**
 * Ссылки за неделю → посевы для отчёта «Отработано».
 *
 * Цифры берутся там же, где для презентации (/api/presentation/media находит
 * пост среди наших аккаунтов и спрашивает Graph API), но ссылок здесь
 * несколько сотен — вся неделя сразу, — поэтому они идут пачками по
 * MEDIA_BATCH_SIZE и о каждой пачке сообщается в onProgress: без этого
 * оператор пять минут смотрит на замерший экран.
 *
 * Посевы берутся из разметки оператора, а если он её не сделал — собираются по
 * подписям. Разложил руками — его блоки идут в отчёт в его порядке и с его
 * темами: он и сеял, ему виднее. Но число посевов в топе действует и тут —
 * тем за неделю бывает за двадцать, а в отчёт просят десять, — и отбираются
 * самые просматриваемые.
 *
 * Ролики TikTok идут в те же посевы: цифры по ним — из Display API
 * подключённого аккаунта (как во вкладке «По ссылкам»), так что ролик чужого
 * аккаунта в отчёт не попадёт, как и пост чужого паблика Instagram.
 *
 * Сеть подаётся снаружи, чтобы разбор, группировка и отбор проверялись тестом.
 */

export type SeedingError = { url: string; error: string };

export type BuildSeedingReportDeps = {
  /** Не больше MEDIA_BATCH_SIZE шорткодов за вызов. */
  fetchMedia: (shortcodes: string[]) => Promise<PresentationMediaResponse>;
  /** Цифры роликов TikTok по ссылкам; не задано — такие ссылки уходят в ошибки. */
  fetchTikTok?: (urls: string[]) => Promise<{ results: MediaStats[]; errors: MediaStatsError[] }>;
};

export type BuildSeedingReportResult = {
  /** Посевы, которые пойдут в файл. */
  top: SeedingGroup[];
  /** Сколько посевов вышло всего — чтобы показать, сколько осталось за бортом. */
  total: number;
  /** Посевы взяты из разметки оператора, а не собраны по подписям. */
  manual: boolean;
  /** Ссылки, которых в отчёте не будет вовсе. */
  errors: SeedingError[];
  /**
   * Ссылки в отчёте, по которым Instagram не отдал статистику. В блок они
   * попадут, но суммы по ним занижены — оператор должен об этом знать.
   */
  noStats: SeedingError[];
};

const STORIES_PATTERN = /instagram\.com\/stories\//i;

/** Ссылка, которую отчёт умеет разобрать: пост Instagram или ролик TikTok. */
export function isSeedingLink(url: string): boolean {
  return Boolean(extractShortcode(url) || parseTikTokVideoUrl(url));
}
const VIDEO_FILE = /\.mp4(\?|$)/i;

/**
 * Картинка для обложки блока. У видео это thumbnail_url — та обложка, что
 * видна в сетке профиля. У фото и карусели thumbnail_url нет, картинка — сам
 * media_url; но у карусели, которая начинается с ролика, media_url — файл
 * видео, и такой адрес не годится.
 */
function coverSource(media: PresentationMediaResponse['media'][number]): string | null {
  if (media.thumbnailUrl) return media.thumbnailUrl;
  if (media.mediaType === 'VIDEO' || !media.mediaUrl || VIDEO_FILE.test(media.mediaUrl)) return null;
  return media.mediaUrl;
}

/** Период отчёта по датам публикаций: от самой ранней до самой поздней. */
export function seedingPeriod(groups: SeedingGroup[]): { from: Date; to: Date } | null {
  const times = groups
    .flatMap((g) => g.items.map((i) => (i.publishedAt ? Date.parse(i.publishedAt) : NaN)))
    .filter((t) => Number.isFinite(t));
  if (times.length === 0) return null;
  return { from: new Date(Math.min(...times)), to: new Date(Math.max(...times)) };
}

export async function buildSeedingReport(
  input: SeedingInput,
  count: number,
  deps: BuildSeedingReportDeps,
  onProgress: (message: string) => void = () => {},
): Promise<BuildSeedingReportResult> {
  const errors: SeedingError[] = [];
  const noStats: SeedingError[] = [];

  // Порядок ссылок внутри посева — порядок ввода; повтор одной публикации
  // выкидываем, иначе её цифры удвоятся в сумме. Повтор считается по всему
  // вводу, а не по блоку: одна публикация не может быть в двух посевах.
  type Link =
    | { kind: 'instagram'; url: string; shortcode: string; block: number }
    | { kind: 'tiktok'; url: string; videoId: string; block: number };
  const links: Link[] = [];
  const seen = new Set<string>();
  input.blocks.forEach((block, index) => {
    for (const raw of block.urls) {
      const url = raw.trim();
      if (!url) continue;
      if (STORIES_PATTERN.test(url)) {
        errors.push({ url, error: 'Сторис в отчёт не идут' });
        continue;
      }
      const video = parseTikTokVideoUrl(url);
      if (video) {
        if (seen.has(`tt:${video.videoId}`)) continue;
        seen.add(`tt:${video.videoId}`);
        links.push({ kind: 'tiktok', url, videoId: video.videoId, block: index });
        continue;
      }
      const shortcode = extractShortcode(url);
      if (!shortcode) {
        errors.push({
          url,
          error: isTikTokUrl(url)
            ? 'Короткая ссылка TikTok — нужна полная, с /video/ и номером ролика'
            : 'Это не ссылка на пост Instagram или ролик TikTok',
        });
        continue;
      }
      if (seen.has(shortcode)) continue;
      seen.add(shortcode);
      links.push({ kind: 'instagram', url, shortcode, block: index });
    }
  });

  if (links.length === 0) return { top: [], total: 0, manual: input.manual, errors, noStats };

  const ordered = links.filter((l): l is Extract<Link, { kind: 'instagram' }> => l.kind === 'instagram');
  const byShortcode = new Map<string, PresentationMediaResponse['media'][number]>();
  const failed = new Map<string, string>();
  for (let i = 0; i < ordered.length; i += MEDIA_BATCH_SIZE) {
    const part = ordered.slice(i, i + MEDIA_BATCH_SIZE);
    onProgress(
      ordered.length > MEDIA_BATCH_SIZE
        ? `Статистика публикаций: ${i + part.length} из ${ordered.length}...`
        : 'Статистика публикаций...',
    );
    const res = await deps.fetchMedia(part.map((o) => o.shortcode));
    for (const m of res.media) byShortcode.set(m.shortcode, m);
    for (const e of res.errors) failed.set(e.shortcode, e.error);
  }

  const tiktokUrls = links.filter((l) => l.kind === 'tiktok').map((l) => l.url);
  const tiktokStats = new Map<string, MediaStats>();
  if (tiktokUrls.length > 0) {
    if (!deps.fetchTikTok) {
      for (const url of tiktokUrls) failed.set(url, 'Ролики TikTok здесь не поддерживаются');
    } else {
      onProgress('Статистика роликов TikTok...');
      const res = await deps.fetchTikTok(tiktokUrls);
      for (const r of res.results) tiktokStats.set(r.url, r);
      for (const e of res.errors) failed.set(e.url, e.error);
    }
  }

  const display = (url: string) => cleanPermalink(/^https?:\/\//i.test(url) ? url : `https://${url}`);
  const items: Array<SeedingItem & { block: number }> = [];
  for (const link of links) {
    const { url, block } = link;
    if (link.kind === 'tiktok') {
      const stats = tiktokStats.get(url);
      if (!stats) {
        errors.push({ url, error: failed.get(url) ?? 'TikTok не вернул статистику' });
        continue;
      }
      items.push({
        block,
        platform: 'tiktok',
        url: display(url),
        shortcode: link.videoId,
        mediaId: link.videoId,
        username: stats.accountUsername,
        caption: stats.caption || null,
        publishedAt: stats.publishedAt || null,
        views: stats.impressionsTotal,
        likes: stats.likes,
        comments: stats.comments,
        reposts: stats.shares,
        coverSource: stats.thumbnailUrl || null,
      });
      continue;
    }

    const media = byShortcode.get(link.shortcode);
    if (!media) {
      errors.push({ url, error: failed.get(link.shortcode) ?? 'Сервер не вернул статистику' });
      continue;
    }
    // Публикацию без цифр из отчёта не выбрасываем: ссылка на неё в блоке
    // посева всё равно нужна, иначе из отчёта пропадёт целый паблик.
    if (media.views === null && media.likes === null) {
      noStats.push({ url, error: media.metricsError ?? 'Instagram не отдал статистику' });
    }
    items.push({
      block,
      url: display(url),
      shortcode: link.shortcode,
      mediaId: media.mediaId,
      username: media.username,
      caption: media.caption,
      publishedAt: media.publishedAt,
      views: media.views,
      likes: media.likes,
      comments: media.comments,
      reposts: media.reposts,
      coverSource: coverSource(media),
    });
  }

  onProgress('Сборка посевов...');

  if (input.manual) {
    // Блоки оператора — его порядок и его темы; из них в отчёт идут count
    // самых просматриваемых, но в том порядке, в каком он их разложил. Блок,
    // от которого не осталось ни одной живой ссылки, пропускаем — причины
    // уже в errors.
    const groups = input.blocks
      .map((block, index) => ({ block, items: items.filter((i) => i.block === index) }))
      .filter(({ items: own }) => own.length > 0)
      .map(({ block, items: own }) => makeSeedingGroup(own, block.theme));
    const picked = new Set(pickTopSeeding(groups, count));
    return { top: groups.filter((g) => picked.has(g)), total: groups.length, manual: true, errors, noStats };
  }

  const groups = groupSeeding(items);
  return {
    top: pickTopSeeding(groups, count),
    total: groups.length,
    manual: false,
    errors,
    noStats,
  };
}
